import React,{useState} from 'react';
import {motion} from 'framer-motion';
import {useNotification} from '../../contexts/NotificationContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const {FiDatabase,FiRefreshCw,FiPlay,FiTrash2,FiCheck,FiX,FiLoader}=FiIcons;

export default function NCBTester() {
  const {showSuccess,showError}=useNotification();
  const [testResults,setTestResults]=useState([]);
  const [isTesting,setIsTesting]=useState(false);
  const [collection,setCollection]=useState('brand_kits');
  const [items,setItems]=useState([]);
  const [meta,setMeta]=useState({});
  const [testData,setTestData]=useState({
    name: 'Test Brand Kit',
    account_id: import.meta.env.VITE_BRAND_ACCOUNT_ID || 'client-main',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  });

  const logResult=(test,status,details=null)=> {
    const result={
      id: Date.now() + Math.random(),
      test,
      status,
      details,
      timestamp: new Date().toISOString()
    };
    setTestResults(prev=> [result,...prev]);
  };

  const logSuccess=(message,details=null)=> {
    logResult(message,'success',details);
  };

  const runTests=async ()=> {
    setIsTesting(true);
    setTestResults([]);
    setItems([]);
    setMeta({});

    try {
      // Test 1: List records with read pattern (query params,no body)
      logResult('List Records','running');
      try {
        const page=1,limit=20;
        const qs=new URLSearchParams({
          page: String(page),
          limit: String(limit),
          includeTotal: 'true'
        }).toString();

        // Use server endpoint instead of direct NCB call
        console.info('[DBG] calling /api/brand-kits ...');
        const res=await fetch(`/api/brand-kits`);
        if(!res.ok) throw new Error(await res.text());
        
        if (import.meta.env.DEV) {
          console.info('[Tester] response',await res.clone().json());
        }
        
        const json=await res.json();
        const items=Array.isArray(json?.data) ? json.data : [];
        const meta=json?.metadata || {};
        
        logResult('List Records','success',json);
        showSuccess(`Retrieved ${items.length} brand kits`);
        setItems(items);
        setMeta(meta);
      } catch (error) {
        logResult('List Records','error',error.message);
        showError('List records test failed: ' + error.message);
      }

      // Test 2: Create record
      logResult('Create Record','running');
      let createdId=null;
      try {
        // Updated payload structure to match NCB requirements - wrapping in brand_kit_configuration
        const payload={
          brand_kit_configuration: JSON.stringify({
            name: testData.name,
            account_id: testData.account_id,
            created_at: testData.created_at || new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
        };
        
        if (import.meta.env.DEV) console.info('[CREATE] payload',payload);

        // Use server endpoint instead of direct NCB call
        console.info('[DBG] calling /api/brand-kits ...');
        const createRes=await fetch('/api/brand-kits',{
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        const createText=await createRes.text();
        let createJson;
        try {
          createJson=JSON.parse(createText);
        } catch {
          throw new Error(createText);
        }
        
        if (import.meta.env.DEV) console.info('[CREATE] raw text',createText,'json',createJson);
        
        createdId=createJson?.id ?? createJson?.data?.id;
        if (!createdId) {
          throw new Error(`No id returned from create — body: ${JSON.stringify(createJson)}`);
        }
        
        logResult('Create Record','success',createJson);
        showSuccess('Create record test passed');

        // Test 3: Get record if created successfully using server proxy
        if (createdId) {
          logResult('Get Record','running');
          try {
            // Use server endpoint instead of direct NCB call
            console.info('[DBG] calling /api/brand-kits ...');
            const res=await fetch(`/api/brand-kits/${createdId}`);
            const text=await res.text();
            let getJson;
            try {
              getJson=JSON.parse(text);
            } catch {
              throw new Error(text);
            }
            
            logResult('Get Record','success',getJson);
            showSuccess('Retrieved brand kit',getJson);

            // Test 4: Update record
            logResult('Update Record','running');
            try {
              const updateData={
                ...testData,
                name: 'Updated Test Brand Kit',
                updated_at: new Date().toISOString()
              };

              // Updated payload structure for update - wrapping in brand_kit_configuration
              const payload={
                brand_kit_configuration: JSON.stringify(updateData)
              };

              // Use server endpoint instead of direct NCB call
              console.info('[DBG] calling /api/brand-kits ...');
              const updateRes=await fetch(`/api/brand-kits/${createdId}`,{
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
              });
              const updateText=await updateRes.text();
              let updateJson;
              try {
                updateJson=JSON.parse(updateText);
              } catch {
                throw new Error(updateText);
              }
              
              logSuccess(`Updated brand kit: ${updateJson?.message || 'OK'}`);
              logResult('Update Record','success',updateJson?.message || 'OK');
              showSuccess('Update record test passed');
            } catch (error) {
              logResult('Update Record','error',error.message);
              showError('Update record test failed: ' + error.message);
            }

            // Test 5: Delete record
            logResult('Delete Record','running');
            try {
              // Use server endpoint instead of direct NCB call
              console.info('[DBG] calling /api/brand-kits ...');
              const delRes=await fetch(`/api/brand-kits/${createdId}`,{
                method: 'DELETE'
              });
              const delText=await delRes.text();
              let delJson;
              try {
                delJson=JSON.parse(delText);
              } catch {
                throw new Error(delText);
              }
              
              logSuccess('Successfully deleted test brand kit');

              // Clear id to avoid post-delete reads
              createdId=null;

              logResult('Delete Record','success',delJson?.message || 'OK');
              showSuccess('Delete record test passed');
            } catch (error) {
              logResult('Delete Record','error',error.message);
              showError('Delete record test failed: ' + error.message);
            }
          } catch (error) {
            logResult('Get Record','error',error.message);
            showError('Get record test failed: ' + error.message);
          }
        }
      } catch (error) {
        logResult('Create Record','error',error.message);
        showError('Create record test failed: ' + error.message);
      }
    } catch (error) {
      showError('Tests failed: ' + error.message);
    } finally {
      setIsTesting(false);
    }
  };

  const clearResults=()=> {
    setTestResults([]);
    setItems([]);
    setMeta({});
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">NCB API Tester</h1>
        <p className="text-gray-600">Test NoCodeBackend API operations for brand kits</p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Test Configuration</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Collection</label>
                <input
                  type="text"
                  value={collection}
                  onChange={(e)=> setCollection(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account ID</label>
                <input
                  type="text"
                  value={testData.account_id}
                  onChange={(e)=> setTestData({...testData,account_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
          <div className="flex space-x-2">
            <motion.button
              whileHover={{scale: 1.02}}
              whileTap={{scale: 0.98}}
              onClick={runTests}
              disabled={isTesting}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                isTesting ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isTesting ? (
                <>
                  <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2 animate-spin" />
                  Running Tests...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiPlay} className="w-4 h-4 mr-2" />
                  Run NCB Tests
                </>
              )}
            </motion.button>
            <motion.button
              whileHover={{scale: 1.02}}
              whileTap={{scale: 0.98}}
              onClick={clearResults}
              className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <SafeIcon icon={FiTrash2} className="w-4 h-4 mr-2" />
              Clear Results
            </motion.button>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-medium text-gray-900">Test Data</h3>
          <textarea
            value={JSON.stringify(testData,null,2)}
            onChange={(e)=> setTestData(JSON.parse(e.target.value))}
            className="w-full h-40 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Test Results</h2>
        {testResults.length > 0 ? (
          <div className="space-y-3">
            {testResults.map((result)=> (
              <div 
                key={result.id} 
                className={`p-4 rounded-lg border-2 ${
                  result.status==='success' ? 'bg-green-50 border-green-200' : 
                  result.status==='error' ? 'bg-red-50 border-red-200' : 
                  result.status==='running' ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex justify-between">
                  <div className="flex items-center">
                    {result.status==='success' && (
                      <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-600 mr-3" />
                    )}
                    {result.status==='error' && (
                      <SafeIcon icon={FiX} className="w-5 h-5 text-red-600 mr-3" />
                    )}
                    {result.status==='running' && (
                      <SafeIcon icon={FiLoader} className="w-5 h-5 text-blue-600 mr-3 animate-spin" />
                    )}
                    <div>
                      <h3 className="font-medium text-gray-900">{result.test}</h3>
                      <p className="text-sm text-gray-600">
                        {new Date(result.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    result.status==='success' ? 'bg-green-100 text-green-800' : 
                    result.status==='error' ? 'bg-red-100 text-red-800' : 
                    result.status==='running' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {result.status.toUpperCase()}
                  </span>
                </div>
                {result.details && (
                  <pre className="mt-3 text-xs text-gray-600 overflow-auto max-h-32 p-2 bg-gray-50 rounded">
                    {typeof result.details==='string' ? result.details : JSON.stringify(result.details,null,2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <SafeIcon icon={FiDatabase} className="w-12 h-12 mx-auto mb-4" />
            <p>Run tests to see results</p>
          </div>
        )}
      </div>
    </div>
  );
}